import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatInTimeZone } from 'date-fns-tz';
import { rates } from '../../data/exchangeRates';
import ConfirmationModal from '../../components/support/ConfirmationModal';
import './Account.css';
const API_URL = process.env.REACT_APP_API_URL;
const CustomerOrderDetailPage = ({ selectedCurrency }) => {
    const { orderId } = useParams();
    const { t, i18n } = useTranslation();
    const { token } = useAuth();
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

     const [customerTimezone, setCustomerTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
const handleViewInvoice = () => {
        if (order.invoice) {
            // Fatura varsa, yeni sekmede aç
            window.open(`${API_URL}/${order.invoice.filepath}`, '_blank');
        } else {
            // Fatura yoksa, uyarı modalını aç
            setIsInvoiceModalOpen(true);
        }
    };
    const fetchPageData = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        try {
            setLoading(true); setError('');
            // Hem sipariş detayını hem de kullanıcı profilini aynı anda çek
            const [orderResponse, profileResponse] = await Promise.all([
                fetch(`${API_URL}/api/jobs/${orderId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            if (!orderResponse.ok) {
                const errorData = await orderResponse.json();
                throw new Error(t(errorData.messageKey) || t('error_fetch_order_details'));
            }
            
            const orderData = await orderResponse.json();
            setOrder(orderData);

            if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                if (profileData.timezone) {
                    setCustomerTimezone(profileData.timezone);
                }
            } else {
                console.warn("Could not fetch customer profile for timezone.");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [orderId, token, t]);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);
    
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return formatInTimeZone(new Date(dateString), customerTimezone, 'dd.MM.yyyy HH:mm');
    };

    const mapStatusForCustomer = (status) => {
        switch (status) {
            case 'pending_approval': case 'available': case 'assigned': case 'in_progress': case 'in_review': return 'in_progress';
            case 'completed': return 'completed';
            case 'cancelled': return 'cancelled';
            default: return 'unknown';
        }
    };

    if (loading) return <div className="page-container"><i className="fas fa-spinner fa-spin"></i> {t('loading_order_details')}</div>;
    if (error) return <div className="page-container error">{error}</div>;
    if (!order) return <div className="page-container">{t('order_not_found')}</div>;

    const sourceFiles = order.files ? order.files.filter(f => f.file_type === 'source') : [];
    const targetFiles = order.files ? order.files.filter(f => f.file_type === 'target') : [];
    const targetLangsArray = Array.isArray(order.target_langs) ? order.target_langs : JSON.parse(order.target_langs);
    const customerStatus = mapStatusForCustomer(order.status);
// YENİ: Fiyatı anlık seçili para birimine çevir
    const priceInTry = order.price;
    const targetRate = rates[selectedCurrency] || 1;
    const convertedPrice = priceInTry / targetRate;
    return (
        // DÜZELTME: Tüm JSX, tek bir Fragment içine alındı.
        <>
		  <div className="page-container account-page narrow-content">
            <header className="detail-page-header">
                <Link to="/account/orders" className="back-button">
                    <i className="fas fa-arrow-left"></i> 
                </Link>
                <h1>{t('order_details')}</h1>
               <div className="header-placeholder"></div>
            </header>

          
                <div className="detail-main-content">
                    <div className="detail-top-bar">
                        <span className="order-id-badge">{t('order_detail_title_with_id', {id: order.id})}</span>
                        <span className={`status-badge-large status-${customerStatus}`}>{t(`customer_status_${customerStatus}`)}</span>
                    </div>
                    
                    {order.status === 'completed' && (
                        <div className="status-bar-info completed">
                            <span>{t('translation_completed')}</span>
                            <button onClick={handleViewInvoice} className="cta-button-gradient">
                                {t('view_invoice')}
                            </button>
                        </div>
                    )}
                    <div className="detail-card-modern files-card">
                    <h3>{t('documents', 'Belgeler')}</h3>
                    <div className="files-table">
                        <div className="files-table-header">
                            <span>{t('source_documents')}</span>
                            <span>{t('translated_documents')}</span>
                        </div>
                        {sourceFiles.length > 0 ? (
                            sourceFiles.map((sourceFile, index) => {
                                const targetFile = targetFiles.find(tf => tf.source_file_id === sourceFile.id);
                                
                                // YENİ: Çevrilmiş dosya için yerelleştirilmiş adı burada oluştur
                                let localizedTargetFileName = '';
                                if (targetFile) {
                                    const fileExtension = targetFile.original_filename.split('.').pop();
                                    localizedTargetFileName = t('generic_file_name', { index: index + 1 }) + `.${fileExtension}`;
                                }

                                return (
                                    <div className="files-table-row" key={sourceFile.id}>
                                        {/* Kaynak Dosya Sütunu */}
                                        <div className="file-cell">
                                            <i className="fas fa-file"></i>
                                            <span className="file-name">{index + 1}. {sourceFile.original_filename}</span>
                                            <a href={`${API_URL}/${sourceFile.filepath}`} download={sourceFile.original_filename} className="download-icon-btn" title={t('download')}>
                                                <i className="fas fa-download"></i>
                                            </a>
                                        </div>
                                        {/* Çevrilmiş Dosya Sütunu */}
                                        <div className="file-cell">
                                            {targetFile ? (
                                                <>
                                                    <i className="fas fa-file-check"></i>
                                {/* DÜZELTME: 'localizedTargetFileName' artık uzantı içermiyor */}
                                <span className="file-name">{localizedTargetFileName}</span>
                                <a href={`${API_URL}/${targetFile.filepath}`} download={targetFile.original_filename} className="download-icon-btn" title={t('download')}>
                                                        <i className="fas fa-download"></i>
                                                    </a>
                                                </>
                                            ) : (
                                                <span className="no-file-text">{t('translation_in_progress')}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : <p className="no-files-message">{t('no_source_files_found')}</p>}
                    </div>
                </div>
                    <div className="detail-card-modern info-card">
                        <div className="info-columns">
                            <div className="info-column">
                                <div className="info-item"><span>{t('word_count')}</span><strong>{order.word_count}</strong></div>
                                <div className="info-item"><span>{t('translation_languages')}</span><strong>{order.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</strong></div>
                                <div className="info-item"><span>{t('order_class')}</span><strong>{t('professional_translator')}</strong></div>
                                <div className="info-item"><span>{t('order_date')}</span><strong>{formatDate(order.created_at)}</strong></div>
                            </div>
                            <div className="info-column">
                                <div className="info-item"><span>{t('job_service_type')}</span><strong>{t('service_type_translation')}</strong></div>
                                <div className="info-item"><span>{t('expertise_area')}</span><strong>{t(order.expertise)}</strong></div>
                                <div className="info-item"><span>{t('delivery_speed')}</span><strong>{t(order.delivery_speed === 'fast' ? 'fast_delivery' : 'standard_delivery')}</strong></div>
                                <div className="info-item"><span>{t('estimated_delivery_end')}</span><strong>{formatDate(order.deadline)}</strong></div>
                            </div>
                        </div>
                        <div className="total-price-section">
                            <span>{t('total_amount')}</span>
                             <strong>
                                {convertedPrice.toLocaleString(i18n.language, {
                                    style:'currency', 
                                    currency: selectedCurrency
                                })}
                            </strong>
                        </div>
                    </div>
                </div>
            </div>
       
<ConfirmationModal
    isOpen={isInvoiceModalOpen}
    onClose={() => setIsInvoiceModalOpen(false)}
    onConfirm={() => setIsInvoiceModalOpen(false)}
    title={t('invoice_not_ready_title')}
    message={t('invoice_not_ready_message')}
    confirmButtonText={t('ok')}
    hideCancelButton={true}
/>
        </>
    );
};

export default CustomerOrderDetailPage;